// Small numeric formatters shared across admin pages. Currency-aware formatting
// for general use lives in `./currencyFormatter` (multi-currency, region-aware) —
// `formatAudCurrency` below is a thin convenience around it for the common
// "AUD, no decimals, en-AU" case so callers don't repeat the args.
import { formatCurrency } from './currencyFormatter';

const NUMBER_FORMATTER = new Intl.NumberFormat('en-AU');

/**
 * Format a value as AUD currency with no decimals (e.g. `$248,200`).
 * Delegates to `formatCurrency` from `./currencyFormatter` so the underlying
 * locale/symbol logic stays canonical; this wrapper just locks the currency
 * and region for ops-tool callers that always want AUD.
 */
export function formatAudCurrency(value: number): string {
  return formatCurrency(value, 'AUD', 'AU', { format: 'rounded' }) ?? '';
}

/**
 * Format a numeric value with en-AU thousands separators. `decimals > 0`
 * preserves that many fractional digits; otherwise the value is rounded to
 * an integer.
 */
export function formatNumber(value: number, decimals = 0): string {
  if (decimals > 0) {
    return value.toLocaleString('en-AU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return NUMBER_FORMATTER.format(Math.round(value));
}

/**
 * Format a signed percent change to one decimal place. Positive values get a
 * leading `+` so the direction is visible at a glance (`+6.2%`, `-16.7%`).
 */
export function formatPercentChange(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

const DECIMAL_FORMATTER = new Intl.NumberFormat('en-AU', { style: 'decimal' });

/**
 * Format a value as a decimal number with en-AU locale grouping
 * (e.g. `1,234.5`). Use `formatNumber` when you also need control over decimal
 * places — this helper just delegates to `Intl.NumberFormat`'s default behaviour.
 */
export function formatDecimal(value: number): string {
  return DECIMAL_FORMATTER.format(value);
}
